if (!window.customElements.get("dac-slideshow")) {
  class DACSlideshow extends HTMLElement {
    connectedCallback() {
      if (this.swiper) return

      this.abortController = new AbortController()
      this.setupEventListeners()
      this.whenReady(() => this.initSwiper())

      this.progressRunId = 0
    }

    disconnectedCallback() {
      this.abortController?.abort()
      this.stopProgress()

      if (this.swiper) {
        this.swiper.destroy(true, true)
        this.swiper = null
      }
    }

    get cells() {
      return Array.from(this.querySelectorAll(".swiper-wrapper > .swiper-slide")).filter(
        (cell) => !cell.hasAttribute("hidden")
      )
    }

    get selectedIndex() {
      if (this.swiper) {
        return this.swiper.realIndex || this.swiper.activeIndex || 0
      }

      const selectedIndex = this.cells.findIndex((cell) => cell.classList.contains("is-selected"))
      return Math.max(selectedIndex, 0)
    }

    get selectedCell() {
      return this.cells[this.selectedIndex]
    }

    get activeSlide() {
      return this.querySelector(".swiper-slide-active") || this.selectedCell
    }

    whenReady(callback) {
      if (window.Swiper) {
        callback()
        return
      }

      const waitForSwiper = () => {
        if (!this.isConnected) return

        if (window.Swiper) {
          callback()
        } else {
          window.requestAnimationFrame(waitForSwiper)
        }
      }

      window.requestAnimationFrame(waitForSwiper)
    }

    setupEventListeners() {
      const { signal } = this.abortController

      this.addEventListener(
        "click",
        (event) => {
          if (!(event.target instanceof Element)) return

          const dotButton = event.target.closest(".page-dots button[data-index]")

          if (dotButton && this.contains(dotButton)) {
            event.preventDefault()
            this.select(Number(dotButton.dataset.index))
            return
          }

          const actionButton = event.target.closest("[data-action]")

          if (!actionButton || !this.contains(actionButton)) return

          switch (actionButton.getAttribute("data-action")) {
            case "navigate-next":
              event.preventDefault()
              this.closest(".shopify-section")?.nextElementSibling?.scrollIntoView({
                block: "start",
                behavior: "smooth"
              })
              break

            case "unmute":
              event.preventDefault()
              this.getActiveVideoMedia().forEach((media) => {
                const video = media.querySelector("video")
                if (video) video.muted = false
              })
              break

            case "mute":
              event.preventDefault()
              this.getActiveVideoMedia().forEach((media) => {
                const video = media.querySelector("video")
                if (video) video.muted = true
              })
              break
          }
        },
        { signal }
      )

      this.addEventListener("volumechange", (event) => this.onVolumeChange(event), {
        capture: true,
        signal
      })

      if (window.Shopify?.designMode) {
        this.addEventListener(
          "shopify:block:select",
          (event) => {
            const slide = event.target.closest(".swiper-slide")
            const index = this.cells.indexOf(slide)

            if (index >= 0) {
              this.select(index, { instant: event.detail.load })
            }

            this.stopProgress()
          },
          { signal }
        )

        this.addEventListener(
          "shopify:block:deselect",
          () => {
            this.startProgress()
          },
          { signal }
        )
      }
    }

    initSwiper() {
      if (this.swiper || !window.Swiper) return

      this.autoplayDelay = Number.parseInt(this.dataset.autoplayDelay, 10) || 0
      this.hasAutoplay = this.autoplayDelay > 0 && this.cells.length > 1
      this.transitionSpeed = Number.parseInt(this.dataset.transitionSpeed, 10) || 600
      this.slideAnimation = this.dataset.animation === "slide" ? "slide" : "fade"

      const swiperOptions = {
        effect: this.slideAnimation,
        rewind: this.cells.length > 1,
        speed: this.transitionSpeed,
        watchOverflow: true,
        allowTouchMove: this.hasAttribute("allow-swipe"),
        pagination: {
          el: this.querySelector(".swiper-pagination"),
          clickable: true
        }
      }

      if (this.slideAnimation === "fade") {
        swiperOptions.fadeEffect = {
          crossFade: true
        }
      }

      this.swiper = new window.Swiper(this, swiperOptions)

      this.syncActiveSlide({ emitChange: false })

      window.requestAnimationFrame(() => {
        this.pauseInactiveMedia()
        this.playActiveMedia()
        this.startProgress()
      })

      this.swiper.on("slideChange", () => {
        this.syncActiveSlide()
        this.startProgress()
      })

      this.swiper.on("slideChangeTransitionEnd", () => {
        this.pauseInactiveMedia()
        this.playActiveMedia()
        this.dispatchCarouselEvent("carousel:settle")
      })
    }

    select(index, { instant = false } = {}) {
      index = Number(index)

      if (!Number.isInteger(index) || index < 0 || index >= this.cells.length) {
        return Promise.resolve()
      }

      this.stopProgress()
      this.dispatchCarouselEvent("carousel:select", index, this.cells[index])

      if (!this.swiper) {
        this.cells.forEach((cell, cellIndex) => {
          cell.classList.toggle("is-selected", cellIndex === index)
        })

        this.syncActiveSlide()
        this.onSlideActive()

        return Promise.resolve()
      }

      this.swiper.slideTo(index, instant ? 0 : this.swiper.params.speed)

      return Promise.resolve()
    }

    next({ instant = false } = {}) {
      if (!this.swiper) {
        return this.select((this.selectedIndex + 1) % this.cells.length, { instant })
      }

      this.stopProgress()
      this.swiper.slideNext(instant ? 0 : this.swiper.params.speed)

      return Promise.resolve()
    }

    previous({ instant = false } = {}) {
      if (!this.swiper) {
        return this.select((this.selectedIndex - 1 + this.cells.length) % this.cells.length, {
          instant
        })
      }

      this.stopProgress()
      this.swiper.slidePrev(instant ? 0 : this.swiper.params.speed)

      return Promise.resolve()
    }

    syncActiveSlide({ emitChange = true } = {}) {
      const activeIndex = this.selectedIndex
      const activeCell = this.selectedCell

      this.stopProgress()
      this.resetProgressBars()

      this.cells.forEach((cell, index) => {
        cell.classList.toggle("is-selected", index === activeIndex)
      })

      this.querySelectorAll(".page-dots button").forEach((button, index) => {
        button.setAttribute("aria-current", index === activeIndex ? "true" : "false")
      })

      this.resetProgressBars()

      if (emitChange && activeIndex !== this.activeIndex) {
        this.dispatchCarouselEvent("carousel:change", activeIndex, activeCell)
      }

      this.activeIndex = activeIndex
    }

    onSlideActive() {
      this.pauseInactiveMedia()
      this.playActiveMedia()
    }

    playActiveMedia() {
      window.requestAnimationFrame(() => {
        const activeSlide = this.activeSlide

        if (!activeSlide) return

        this.getActiveVideoMedia(activeSlide).forEach((media) => {
          media.play?.()
        })
      })
    }

    pauseInactiveMedia() {
      const activeSlide = this.activeSlide

      this.querySelectorAll(".swiper-slide video-media").forEach((media) => {
        if (activeSlide?.contains(media)) return

        media.pause?.()
      })
    }

    getActiveVideoMedia(slide = this.activeSlide) {
      return Array.from(slide?.querySelectorAll("video-media") || [])
    }

    startProgress() {
      if (!this.hasAutoplay || window.Shopify?.designMode) return

      this.stopProgress()
      this.updateProgress(0)

      const runId = this.progressRunId
      const startedAt = window.performance.now()
      const duration = Math.max(this.autoplayDelay, 1)

      const update = (now) => {
        if (runId !== this.progressRunId) return

        const progress = (now - startedAt) / duration

        this.updateProgress(progress)

        if (progress >= 1) {
          this.updateProgress(1)

          this.progressFrame = window.requestAnimationFrame(() => {
            if (runId !== this.progressRunId) return
            this.next()
          })

          return
        }

        this.progressFrame = window.requestAnimationFrame(update)
      }

      this.progressFrame = window.requestAnimationFrame(update)
    }

    stopProgress() {
      this.progressRunId += 1

      if (!this.progressFrame) return

      window.cancelAnimationFrame(this.progressFrame)
      this.progressFrame = null
    }

    resetProgressBars() {
      this.querySelectorAll(".swiper-pagination-bullet").forEach((bullet) => {
        bullet.style.setProperty("--slideshow-progress", "0")
      })
    }

    updateProgress(progress) {
      const activeButton = this.querySelector(".swiper-pagination-bullet-active")

      if (!activeButton) return

      activeButton.style.setProperty(
        "--slideshow-progress",
        String(Math.min(Math.max(progress || 0, 0), 1))
      )
    }

    onVolumeChange(event) {
      const slide = event.target.closest(".swiper-slide")
      const volumeControl = slide?.querySelector(".slideshow__volume-control")

      if (!volumeControl) return

      volumeControl.querySelector('[data-action="unmute"]').hidden = !event.target.muted
      volumeControl.querySelector('[data-action="mute"]').hidden = event.target.muted
    }

    dispatchCarouselEvent(name, index = this.selectedIndex, cell = this.selectedCell) {
      if (!cell) return

      this.dispatchEvent(
        new CustomEvent(name, {
          detail: {
            index,
            cell
          }
        })
      )
    }
  }

  window.customElements.define("dac-slideshow", DACSlideshow)
}
